import type { TFunction } from "i18next";
import { useState } from "react";
import type { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U3121Employee } from "../../../../U3121Service";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U3121ProjectFormExtendedHandlers } from "../u3121ProjectContactsTab/U3121ProjectContactHandlers";

const NS = "u3121";

export interface U3121ProjectTimesheetAccessHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onRowClick: (row: U3121Employee) => void;
  onDlgClose: (result: string) => void;
  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3121Employee>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3121Employee>>;
  dataChangedEvent: number;
}

export interface U3121ProjectTimesheetAccessHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3121Employee>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3121Employee>>;
  parentFormHandlers: U3121ProjectFormExtendedHandlers;
}

export function u3121ProjectTimesheetAccessHandler(params: U3121ProjectTimesheetAccessHandlerParam): U3121ProjectTimesheetAccessHandlerReturn {
  const { gridStateHandlers, formStateHandlers, parentFormHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);

  const onAdd = (): void => {
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    formStateHandlers.setData({ id: "", firstName: "", lastName: "", codeEmployee: "" });
    setDialogOpen(true);
  };

  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0];
    if (selectedRow) {
      formStateHandlers.setData({ ...selectedRow });
    }
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
  };

  const onDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  const onRowClick = (row: U3121Employee): void => {
    formStateHandlers.setData(row);
    const parentFormState = parentFormHandlers.getFormState?.();
    if (parentFormState === U2000FormStateEnumState.EDIT) {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    } else {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.NONE);
    }
    // Pas d'ouverture du dialogue : on ne fait que sélectionner la ligne (pour suppression éventuelle).
  };

  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
    } else if (result === "save") {
      const businessHandlers = formStateHandlers.getBusinessHandlers();
      if (businessHandlers?.validateForm) {
        const errors = businessHandlers.validateForm(formStateHandlers.getData());
        formStateHandlers.setErrors(errors);
        formStateHandlers.saveData();
        if (Object.keys(errors).length > 0) return;
      }

      const formData = formStateHandlers.getData();
      const employeeId = formData?.id?.trim() ?? "";
      if (!employeeId) {
        formStateHandlers.setErrors({ id: params.t("errTimesheetAccessEmployeeRequired", { ns: NS }) });
        return;
      }

      const normalized: U3121Employee = {
        id: formData!.id,
        codeEmployee: formData!.codeEmployee,
        firstName: formData!.firstName,
        lastName: formData!.lastName,
      };

      const existingData = gridStateHandlers.getData();
      const isEdit = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT;
      const selectedRow = gridStateHandlers.getSelectedRows()[0];
      const excludeId = isEdit && selectedRow ? selectedRow.id : undefined;

      const duplicate = existingData.some((row) => row.id === normalized.id && row.id !== excludeId);
      if (duplicate) {
        formStateHandlers.setErrors({ id: params.t("errTimesheetAccessEmployeeDuplicate", { ns: NS }) });
        setDialogOpen(false);
        return;
      }

      if (isEdit && excludeId) {
        const updatedData = existingData.map((item) => (item.id === excludeId ? normalized : item));
        gridStateHandlers.setData(updatedData);
        setDataChangedEvent((prev) => prev + 1);
      } else {
        gridStateHandlers.setData([...existingData, normalized]);
        gridStateHandlers.setTotalRows(existingData.length + 1);
        setDataChangedEvent((prev) => prev + 1);
      }
      formStateHandlers.afterSaveData(formStateHandlers.getData());
    }
    setDialogOpen(false);
  };

  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);
      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent((prev) => prev + 1);
    }
    setDeleteDialogOpen(false);
  };

  return {
    onAdd,
    onModify,
    onDelete,
    onRowClick,
    onDlgClose,
    isDialogOpen,
    setDialogOpen,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    formStateHandlers,
    gridStateHandlers,
    dataChangedEvent,
  };
}
